import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, ExternalLink, Tag } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@baluu/ui/components/card";
import { Separator } from "@baluu/ui/components/separator";
import { NoteMarkdown } from "@baluu/ui/components/checkout/note-markdown";
import { CloudinaryUrl__TransformedUrl } from "@obby/lib";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson } from "../api";
import { BookingLoading, BookingProblem, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { BulletList, HostNote } from "./manage/parts";
import {
  instructionsOf,
  isLongText,
  learningLinks,
  purchaseDate,
  schoolNameOf,
  successPageEvent,
  type ProductSuccessPage,
} from "./success/productSuccess";

// /payment/product/success/<purchase>: the receipt after buying a product
// (a kit). Port of v1's payment/product/success/[productPurchaseID] (page,
// getProductPurchaseData, ProductPurchaseSuccessClient, ProductReceiptCard,
// SimpleBanner/BulletPointsList): the product's name, its "Get Ready" tab
// (the school's note, the learning guides and videos, their instructions,
// what's in the kit) beside the purchase's details and "Buy Again". The
// purchase id is what lets the customer in, as on v1 (GET
// /widget/pages/payment/product/success/:id, no sign-in). v1's mount-time
// "Success Page" dataLayer entry is pushed the same way.
//
// v1 hid the purchase card below its `lg` breakpoint; here it shows on
// every width (under the tab on a phone) so the purchase number is never
// out of reach. v1's Obby background artwork and avatar are left out, as on
// the manage page: the site's theme colours the note.

function sizedImage(url: string | undefined, width: number, height: number): string | undefined {
  if (!url || !/^https?:\/\//.test(url)) return undefined;
  return CloudinaryUrl__TransformedUrl(url, { width, height, crop: "fill" }) as string;
}

export function ProductSuccessView({ ctx, purchaseId }: { ctx: ViewContext; purchaseId: string }) {
  const page = useQuery({
    queryKey: ["booking", "product-success", ctx.api, purchaseId],
    queryFn: ({ signal }) =>
      bookingJson<ProductSuccessPage>(`${ctx.api}/widget/pages/payment/product/success/${encodeURIComponent(purchaseId)}`, { signal }),
    retry: (count, error) => count < 1 && !(error instanceof BookingApiError),
  });

  if (page.isPending) return <BookingLoading rows={3} />;
  if (page.isError) {
    const notFound = page.error instanceof BookingApiError && page.error.status === 404;
    return notFound ? (
      <RecordNotFound what="purchase" />
    ) : (
      <BookingProblem title="Your receipt couldn’t be loaded" message="Please try again in a moment." onRetry={() => void page.refetch()} />
    );
  }
  const purchase = page.data?.productPurchase;
  if (!purchase?.product) return <RecordNotFound what="purchase" />;
  if (isOtherSchool(ctx.businessId, page.data.school, purchase.school)) return <RecordNotFound what="purchase" />;
  return <Receipt ctx={ctx} page={page.data} />;
}

function Receipt({ ctx, page }: { ctx: ViewContext; page: ProductSuccessPage }) {
  const purchase = page.productPurchase!;
  const product = purchase.product!;
  const schoolName = schoolNameOf(page);
  const links = learningLinks(product.learningContent);
  const instructions = instructionsOf(product.learningContent);
  const included = product.whatsIncluded ?? [];
  const cover = sizedImage(product.cover, 248, 166);

  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer?.push(
      successPageEvent(page, {
        path: window.location.pathname || "",
        referrer: document.referrer || "",
        title: document.title || "",
        url: window.location.href,
      }),
    );
  }, [page]);

  const details = (
    <Card className="border-t-4 border-t-primary border-border/60 shadow-sm">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <CardTitle className="text-lg uppercase tracking-wide">Your purchase details</CardTitle>
        {cover && (
          // eslint-disable-next-line @next/next/no-img-element -- a web component, not a Next page
          <img src={cover} alt="" className="hidden w-[124px] shrink-0 rounded-md object-cover sm:block" />
        )}
      </CardHeader>
      <Separator />
      <CardContent className="space-y-3 pt-5 text-sm">
        <p className="flex items-center gap-2">
          <Tag className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span>
            Purchase no: <span className="break-all font-medium">{purchase._id}</span>
          </span>
        </p>
        {purchaseDate(purchase.createdAt) && (
          <p className="flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>Date purchased: {purchaseDate(purchase.createdAt)}</span>
          </p>
        )}
      </CardContent>
      {product.slug && (
        <CardFooter>
          <Button asChild variant="outline" className="w-full">
            <a href={ctx.links.product(product.slug)}>Buy Again</a>
          </Button>
        </CardFooter>
      )}
    </Card>
  );

  return (
    <div className="space-y-8">
      <header>
        <span className="text-sm font-medium uppercase tracking-widest text-primary">Your receipt page for:</span>
        <h1 className="mt-2 text-3xl font-semibold uppercase leading-tight tracking-tight sm:text-4xl">{product.name}</h1>
      </header>

      <div className="grid gap-8 lg:grid-cols-3">
        <section className="space-y-6 lg:col-span-2" aria-labelledby="get-ready">
          <div className="border-b">
            <h2 id="get-ready" className="inline-block border-b-2 border-primary pb-3 text-base font-medium">
              Get Ready
            </h2>
          </div>

          <HostNote title="Hey, here is some info about your product" text={product.purchaseMessage} name={schoolName || undefined} />

          {links.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {links.map((link, i) => (
                <Card key={i} className="border-t-4 border-t-primary border-border/60 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base font-medium leading-snug">{link.title}</CardTitle>
                  </CardHeader>
                  {link.href && (
                    <CardFooter>
                      <a
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline"
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.label}
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                      </a>
                    </CardFooter>
                  )}
                </Card>
              ))}
            </div>
          )}

          {instructions.map((text, i) => (
            <Instructions key={i} text={text} />
          ))}

          {included.length > 0 && <BulletList title="What is included in your kit?" items={included} />}
        </section>

        <aside className="lg:col-span-1">{details}</aside>
      </div>
    </div>
  );
}

/** v1's "Instructions" banner: the school's Markdown, folded past 100
 * words behind "Read more" (ExpandableText). */
function Instructions({ text }: { text: string }) {
  const long = isLongText(text);
  const [open, setOpen] = useState(false);
  return (
    <Card className="border-t-4 border-t-primary border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">Instructions</CardTitle>
      </CardHeader>
      <CardContent>
        <NoteMarkdown className={`space-y-2 text-sm leading-relaxed text-foreground ${long && !open ? "line-clamp-6" : ""}`}>{text}</NoteMarkdown>
      </CardContent>
      {long && (
        <CardFooter>
          <Button variant="link" className="h-auto p-0" onClick={() => setOpen(!open)} aria-expanded={open}>
            Read {open ? "less" : "more"}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
